import { Component, computed, input, model, OnInit, output, signal } from '@angular/core';
import { MaterialModel, OrderByPipe } from '@evorait/pace-core';
import { SmartlockerData } from '../../../../smartlocker/interfaces/smartlocker-doors.interface';
import {
    BOOTSTOCK_RETURN_ACTION,
    getBootstockReturnAction,
} from '../../../../core/services/bootstock-return/bootstock-return.interface';
import { SmartlockerService } from '../../../../smartlocker/services/smartlocker.service';
import { of, tap } from 'rxjs';
import { BootstockReturnService } from '../../../../core/services/bootstock-return/bootstock-return.service';
import { catchError, finalize } from 'rxjs/operators';

/** Constant action order for sorting - defined outside class to avoid recreation per call */
const ACTION_ORDER: Readonly<Record<BOOTSTOCK_RETURN_ACTION, number>> = {
    [BOOTSTOCK_RETURN_ACTION.NONE]: 1,
    [BOOTSTOCK_RETURN_ACTION.RETURN]: 2,
    [BOOTSTOCK_RETURN_ACTION.SCRAP]: 3,
} as const;

@Component({
    selector: 'pace-confirmation-material-stock-details',
    templateUrl: './confirmation-material-stock-details.component.html',
    standalone: false,
})
export class ConfirmationMaterialStockDetailsComponent implements OnInit {
    public materialsList = model.required<MaterialModel[]>();
    public bootStock = input<MaterialModel[]>();
    public isModelNotification = input.required<boolean>();
    public isBootStock = input<boolean>(false);
    /** Return/scrap is only offered when cleaning up boot stock from the side menu, not while consuming material */
    public showBootStockActions = input<boolean>(false);
    public orderField = signal<{ oldValue: string; newValue: string }>({ oldValue: '', newValue: '' });
    public smartLockers = signal<SmartlockerData[]>([]);
    public isBootStockScrapable = computed<boolean>(() =>
        this.materialsList().some((material) => material.isBootStockScrapable),
    );
    public bootStockReturnIds = signal<Map<string, boolean>>(new Map<string, boolean>());
    public isLoadingBootStockReturnIds = signal<boolean>(false);

    public showBootStockActionColumn = computed<boolean>(
        () =>
            this.isBootStock() &&
            this.showBootStockActions() &&
            (this.isLoadingBootStockReturnIds() || this.bootStockReturnIds().size > 0 || this.isBootStockScrapable()),
    );
    public isReverse = signal<boolean>(false);

    public bootStockReturnSuccess = output<void>();
    public smartLockerPickup = output<void>();
    public switchToBootStock = output<void>();
    public sortField = output<string>();

    private readonly orderByPipe = new OrderByPipe();
    public displayMaterials = computed<MaterialModel[]>(() => {
        if (this.orderField().newValue === 'bootStockAction') {
            return this.sortByAction(this.materialsList(), this.bootStockReturnIds(), this.isReverse());
        } else if (this.orderField()) {
            return this.orderByPipe.transform(this.materialsList(), this.orderField().newValue, this.isReverse()) ?? [];
        } else {
            return this.materialsList();
        }
    });

    public constructor(
        private readonly smartLockerService: SmartlockerService,
        private readonly bootstockReturnService: BootstockReturnService,
    ) {}

    public ngOnInit(): void {
        if (this.isBootStock()) {
            if (!this.showBootStockActions()) {
                return;
            }
            this.isLoadingBootStockReturnIds.set(true);
            this.bootstockReturnService
                .getBootstockReturnIds(this.materialsList())
                .pipe(
                    tap((bootStockReturnIds) => this.bootStockReturnIds.set(bootStockReturnIds)),
                    catchError(() => {
                        // Return empty Map on error to stop the loading indicator.
                        return of(new Map<string, boolean>());
                    }),
                    finalize(() => this.isLoadingBootStockReturnIds.set(false)),
                )
                .subscribe();
        } else {
            this.smartLockerService
                .getSmartlockers()
                .pipe(tap((smartlockers) => this.smartLockers.set(smartlockers)))
                .subscribe();
        }
    }

    /**
     * Sorts materials by bootstock action
     *
     * @param materials the materials to sort
     * @param bootStockReturnIds map of material numbers to return status
     * @param isReverse whether to reverse the sort order
     * @returns sorted materials
     */
    private sortByAction(
        materials: MaterialModel[],
        bootStockReturnIds: Map<string, boolean>,
        isReverse: boolean,
    ): MaterialModel[] {
        return [...materials].sort((a, b) => {
            const actionA = getBootstockReturnAction(a, bootStockReturnIds);
            const actionB = getBootstockReturnAction(b, bootStockReturnIds);
            const comparison = ACTION_ORDER[actionA] - ACTION_ORDER[actionB];
            return isReverse ? -comparison : comparison;
        });
    }

    /**
     * Sorting the table
     *
     * @param field the field to sort
     */
    public sortFieldBy(field: string): void {
        const previousField = this.orderField().newValue;
        this.orderField.set({ oldValue: previousField, newValue: field });
        this.isReverse.set(previousField === field ? !this.isReverse() : false);
        this.sortField.emit(this.orderField().newValue);
    }
}
